import { IoIosAdd, IoIosRemove } from "react-icons/io";
import { darken, tint } from "polished";
import { Link } from "react-router-dom";
import styled from "styled-components";

const base = (props) => props.theme.colors.primary;

export const Card = styled.div`
  width: 100%;
  margin: 0 auto 12px;
  padding: 18px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 18px;
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.neutral100};
  border-radius: 16px;
  box-shadow: var(--cards);
  transition: box-shadow 200ms ease, border-color 200ms ease;

  &:hover {
    border-color: ${(props) => tint(0.85, base(props))};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.09);
  }

  @media (max-width: 660px) {
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px;
  }
`;

export const ThumbnailWrap = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  justify-content: center;
  align-items: center;
  background: #f5f6fa;
  border-radius: 12px;
  padding: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 660px) {
    width: 72px;
    height: 72px;
  }
`;

export const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 6px;

  @media (max-width: 660px) {
    flex: 1 1 55%;
  }
`;

export const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 6px;

  button.special-conditions {
    width: 145px;
    height: 30px;
    border: none;
    color: ${(props) => props.theme.colors.bodyDark};
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.3rem;
    text-decoration: none;
    font-weight: 400;
    background: ${(props) => base(props)};
    border-radius: 3px;
    transition: background 200ms ease-in-out;

    :hover {
      background: ${(props) => darken(0.05, base(props))};
    }
  }
`;

export const ProductTitle = styled(Link)`
  font-family: 'DM Sans', system-ui, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 15px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.neutral900};
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: color 200ms ease;

  &:hover {
    color: ${(props) => base(props)};
  }
`;

export const ProductAvailabilityInfo = styled.p`
  color: ${props => props.theme.colors.primary};
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
`;

export const ProductValue = styled.div`
  div.fidelity-price-container {
    display: flex;
    flex-direction: column;

    p.old-price-text {
      color: ${(props) => props.theme.colors.neutral600};
      text-align: left;
      font-size: 12px;
      font-weight: 500;
      line-height: 18.5px;
    }

    span.old-price {
      color: ${(props) => props.theme.colors.neutral600};
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 18.5px;
      text-align: left;
      text-decoration: line-through;
    }

    div.fidelity-price {
      display: flex;
      align-items: center;
      justify-content: flex-start;

      span.new-price {
        color: ${(props) => props.theme.colors.neutral900};
        font-family: 'Sora', system-ui, sans-serif;
        font-style: normal;
        font-weight: 700;
        font-size: 18px;
        line-height: normal;
      }

      img {
        width: 40px;
        height: 25px;
        margin-left: 10px;
      }
    }
  }
`;

export const ProductActions = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 12px;

  @media (max-width: 660px) {
    flex: 1 1 auto;
    align-items: flex-end;
  }
`;

export const QuantityWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  background: ${(props) => props.theme.colors.neutral50};
  border: 1px solid ${(props) => props.theme.colors.neutral100};
  border-radius: 999px;
  padding: 4px;

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }

  input {
    width: 34px;
    height: 28px;
    text-align: center;
    outline: none;
    font-size: 15px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.neutral900};
    border: none;
    background: transparent;
  }

  button {
    color: ${(props) => base(props)};
    fill: ${(props) => base(props)};
    background: ${(props) => props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.neutral100};
    border-radius: 999px;
    width: 28px;
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background 200ms ease;

    &:hover {
      background: ${(props) => tint(0.9, base(props))};
    }

    &:disabled {
      cursor: not-allowed;
    }
  }
`;

export const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${(props) => props.theme.colors.neutral600};
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  transition: color 200ms ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${(props) => props.theme.colors.error};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const RemoveIcon = styled(IoIosRemove)``;

export const AddIcon = styled(IoIosAdd)``;

export const Discount = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span {
    color: ${(props) => props.theme.colors.white};
    background: ${(props) => base(props)};
    text-align: center;
    padding: 4px 10px;
    font-size: 1.2rem;
    font-weight: 500;
    border-radius: 999px;
  }
`;

export const SpecialPriceContainer = styled.div`
  &.isDesktop {
    display: none;

    @media (min-width: 661px) {
      display: flex;
    }
  }
  &.isMobile {
    @media (min-width: 661px) {
      display: none;
    }
  }
  .special-prices-container {
    display: flex;
    align-items: center;
    gap: 5px;

    @media (max-width: 660px) {
      flex-direction: column;
    }
  }

  .biggest-discount {
    width: 100%;
    max-width: 120px;
    background-color: ${(props) => props.theme.colors.promotion};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;

    span {
      color: ${(props) => props.theme.colors.white};
      font-size: 1.2rem;
      padding: 4px 10px;
      text-align: center;
    }
  }
`;

export const SpecialPrice = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 120px;

  span {
    color: ${(props) => props.theme.colors.bodyDark};
    background: ${(props) => props.theme.colors.secondary};
    text-align: center;
    padding: 4px 10px;
    font-size: 1.2rem;
    font-weight: 500;
    border-radius: 6px;
    text-transform: uppercase;
  }

  @media (max-width: 660px) {
    justify-content: flex-start;
    width: 100%;
  }
`;
